import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '@smarty/shared';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { devLog } from '@/lib/utils';
import { getCachedProductsIfFresh, cacheProducts } from '@/lib/db';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
// Polaris imports
import { Page, Card, TextField, Button, Badge, Spinner, Icon, InlineStack, Text, BlockStack, Box, InlineGrid, Thumbnail } from '@/lib/polaris';
import { ProductIcon, SearchIcon } from '@/lib/polaris-icons';
import { ROUTES } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url: string;
  stock: number;
  is_active: boolean;
}

export default function MenuPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  // Cargar solo productos activos
  const loadProducts = useCallback(async () => {
    try {
      // Si estamos OFFLINE, usar caché SIN validación de frescura
      if (!navigator.onLine) {
        const { getCachedProducts } = await import('@/lib/db');
        const offlineCache = await getCachedProducts<Product>();
        
        if (offlineCache.length === 0) {
          setProducts([]);
          setLoading(false);
          return;
        }

        // Filtrar solo activos
        const activeProducts = offlineCache.filter((p) => p.is_active);
        setProducts(activeProducts);
        setLoading(false);
        return;
      }

      // Validar autenticación y tenantId
      if (authLoading) {
        return; // Esperar a que termine de cargar
      }

      if (!tenantId) {
        throw new Error('No se encontró el tenant. Por favor inicia sesión nuevamente.');
      }

      // ONLINE: Mostrar caché solo si es SUPER FRESCO
      const cached = await getCachedProductsIfFresh<Product>();
      const cachedActive = cached.filter((p) => p.is_active);
      
      let shouldShowCache = false;
      
      if (cachedActive.length > 0) {
        // Mostrar caché primero (UX instantánea)
        setProducts(cachedActive);
        setLoading(false);
        shouldShowCache = true;
      } else {
        setLoading(true);
      }

      // Cargar desde Supabase en paralelo (fuente de verdad)
      const { data, error } = await supabase
        .from('products')
        .select('id,name,description,price,category,image_url,stock,is_active,tenant_id')
        .eq('tenant_id', tenantId)
        .eq('is_active', true) // Solo productos activos
        .order('category')
        .order('name');

      if (error) throw error;

      const productsData = data || [];
      
      // Comparar caché con datos frescos
      if (shouldShowCache && cachedActive.length > 0) {
        const cachedIds = new Set(cachedActive.map((p) => p.id).sort());
        const freshIds = new Set(productsData.map((p) => p.id).sort());
        
        const idsMatch = cachedIds.size === freshIds.size && 
          Array.from(cachedIds).every(id => freshIds.has(id));
        
        if (!idsMatch) {
          // Los productos son diferentes, actualizar UI
          setProducts(productsData);
        }
        // Si los productos son los mismos, NO actualizar UI para evitar re-render innecesario
      } else {
        // No había caché o no se mostró, actualizar directamente
        setProducts(productsData);
      }

      // Actualizar caché con productos frescos (siempre)
      await cacheProducts(productsData);
    } catch (error: unknown) {
      devLog.error('❌ Error al cargar menú:', error);
      // Fallback a caché
      try {
        const { getCachedProducts } = await import('@/lib/db');
        const cached = await getCachedProducts<Product>();
        const activeProducts = cached.filter((p) => p.is_active);
        if (activeProducts.length > 0) {
          setProducts(activeProducts);
        }
      } catch (cacheError) {
        devLog.error('Error al cargar caché:', cacheError);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, authLoading]);

  // Debounce de búsqueda para evitar filtrado excesivo
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  // Filtrar productos
  useEffect(() => {
    let filtered = products;

    // Filtrar por categoría
    if (selectedCategory !== 'Todos') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Filtrar por búsqueda (usar debounced)
    if (debouncedSearchQuery.trim()) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
      );
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, debouncedSearchQuery]);

  // useEffect para cargar productos cuando auth esté listo
  useEffect(() => {
    if (!authLoading && tenantId) {
      loadProducts();
    }
  }, [authLoading, tenantId, loadProducts]);

  // Obtener categorías únicas de productos activos
  const categories = ['Todos', ...new Set(products.map((p) => p.category))];

  if (loading) {
    return (
      <Page
        title={
          <PageBreadcrumbTitle
            icon={ProductIcon}
            title="Menú"
            onBack={() => storeNavigate(ROUTES.PRODUCTS)}
            backAccessibilityLabel="Volver a Productos"
          />
        }
      >
        <Card>
          <Box padding="800" minHeight="240px">
            <InlineStack align="center" blockAlign="center">
              <BlockStack gap="300" inlineAlign="center">
                <Spinner size="large" accessibilityLabel="Cargando menú" />
                <Text as="p" tone="subdued">Cargando menú...</Text>
              </BlockStack>
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={ProductIcon}
          title="Menú"
          onBack={() => storeNavigate(ROUTES.PRODUCTS)}
          backAccessibilityLabel="Volver a Productos"
        />
      }
    >
      <BlockStack gap="600">
        <Text as="p" tone="subdued">
          {filteredProducts.length} productos disponibles
        </Text>

        {/* Search and Category Filter */}
        <BlockStack gap="400">
        <Box maxWidth="400px">
          <TextField
            label=""
            labelHidden
            placeholder="Buscar en el menú..."
            value={searchQuery}
            onChange={setSearchQuery}
            prefix={<Icon source={SearchIcon} />}
            autoComplete="off"
          />
        </Box>

        <InlineStack gap="200" wrap>
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'primary' : 'secondary'}
              size="slim"
            >
              {category}
            </Button>
          ))}
        </InlineStack>
      </BlockStack>

        {/* Products Grid */}
        <Card>
          {filteredProducts.length === 0 ? (
            <Box padding="1200">
              <BlockStack gap="400" inlineAlign="center">
                <Text as="p" variant="headingMd" tone="subdued">
                  {products.length === 0 ? 'No hay productos en el menú' : 'No se encontraron productos'}
                </Text>
                <Text as="p" tone="subdued">🍽️</Text>
              </BlockStack>
            </Box>
          ) : (
            <Box padding="400">
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="400">
                {filteredProducts.map((product) => (
                  <Card key={product.id}>
                    <BlockStack gap="300">
                      <Box minHeight="12rem" background="bg-surface-secondary" padding="400">
                        <InlineStack align="center" blockAlign="center">
                          {product.image_url && (product.image_url.startsWith('http') || product.image_url.startsWith('/')) ? (
                            <Thumbnail source={product.image_url} alt={product.name} size="large" />
                          ) : (
                            <Text as="span" variant="heading2xl">🍽️</Text>
                          )}
                        </InlineStack>
                      </Box>
                      <BlockStack gap="200">
                        <InlineStack gap="200" align="space-between" blockAlign="center" wrap={false}>
                          <Text as="h3" variant="headingMd" fontWeight="semibold">
                            {product.name}
                          </Text>
                          <Badge tone="info">{product.category}</Badge>
                        </InlineStack>
                        {product.description && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {product.description.slice(0, 80)}{product.description.length > 80 ? '...' : ''}
                          </Text>
                        )}
                        <InlineStack gap="200" align="space-between" blockAlign="center">
                          <Text as="span" variant="headingMd" fontWeight="bold">
                            S/ {product.price.toFixed(2)}
                          </Text>
                          {product.stock <= 5 && product.stock > 0 && (
                            <Badge tone="warning">Stock bajo</Badge>
                          )}
                        </InlineStack>
                      </BlockStack>
                    </BlockStack>
                  </Card>
                ))}
              </InlineGrid>
            </Box>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}
